import React, { useState, useMemo, useCallback } from 'react';
import ShowcasePageWrapper from '../shared/ShowcasePageWrapper';
import { generateCalendarEvents, SLDS_COLORS } from '../shared/SampleData';
import Button from '@salesforce/design-system-react/components/button';
import ButtonGroup from '@salesforce/design-system-react/components/button-group';
import Icon from '@salesforce/design-system-react/components/icon';
import Modal from '@salesforce/design-system-react/components/modal';
import Input from '@salesforce/design-system-react/components/input';
import Combobox from '@salesforce/design-system-react/components/combobox';

// ─── Constants ─────────────────────────────────────────────────────────────────
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const HOUR_OPTIONS = Array.from({ length: 13 }, (_, i) => {
  const h = i + 7;
  const label = h <= 12 ? `${h}:00 AM` : `${h - 12}:00 PM`;
  return { id: String(h), label };
});
const MAX_VISIBLE_EVENTS = 3;

// ─── Style objects ─────────────────────────────────────────────────────────────
const cardStyle = {
  backgroundColor: 'white',
  border: '1px solid #dddbda',
  borderRadius: '4px',
  padding: '20px',
};

const toolbarStyle = {
  ...cardStyle,
  marginBottom: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '12px',
};

const sidebarStyle = {
  width: '250px',
  minWidth: '250px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
};

const dayHeaderStyle = {
  padding: '8px 4px',
  textAlign: 'center',
  fontWeight: '700',
  fontSize: '12px',
  color: '#706e6b',
  textTransform: 'uppercase',
};

const eventBarStyle = {
  fontSize: '11px',
  fontWeight: '600',
  color: 'white',
  padding: '2px 6px',
  borderRadius: '3px',
  marginBottom: '2px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
};

const hourLabelStyle = {
  width: '60px',
  minWidth: '60px',
  textAlign: 'right',
  paddingRight: '8px',
  fontSize: '11px',
  color: '#706e6b',
  fontWeight: '500',
};

// ─── Helper functions ──────────────────────────────────────────────────────────
const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

const formatHour = (h) => {
  if (h === 0) return '12:00 AM';
  if (h < 12) return `${h}:00 AM`;
  if (h === 12) return '12:00 PM';
  return `${h - 12}:00 PM`;
};

const padDate = (year, month, day) =>
  `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

// ─── Main component ────────────────────────────────────────────────────────────
const CalendarScheduling = () => {
  // Fixed to July 2025 to align with generateCalendarEvents
  const [currentYear, setCurrentYear] = useState(2025);
  const [currentMonth, setCurrentMonth] = useState(6); // July = 6 (zero-indexed)
  const [viewMode, setViewMode] = useState('month'); // month | week | day
  const [selectedWeekStart, setSelectedWeekStart] = useState(6); // July 6 (Sunday)
  const [selectedDay, setSelectedDay] = useState(15); // July 15
  const [events, setEvents] = useState(() => generateCalendarEvents());
  const [showNewEventModal, setShowNewEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [draggedEvent, setDraggedEvent] = useState(null);

  // New event form state
  const [newEventTitle, setNewEventTitle] = useState('');
  const [newEventDate, setNewEventDate] = useState('2025-07-15');
  const [newEventStartTime, setNewEventStartTime] = useState([]);
  const [newEventEndTime, setNewEventEndTime] = useState([]);
  const [newEventLocation, setNewEventLocation] = useState('');
  const [newEventDescription, setNewEventDescription] = useState('');

  // ─── Calendar grid computation ───────────────────────────────────────────────
  const calendarGrid = useMemo(() => {
    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
    const prevMonthDays = getDaysInMonth(
      currentMonth === 0 ? currentYear - 1 : currentYear,
      currentMonth === 0 ? 11 : currentMonth - 1
    );

    const cells = [];

    // Previous month padding
    for (let i = firstDay - 1; i >= 0; i--) {
      const day = prevMonthDays - i;
      const m = currentMonth === 0 ? 11 : currentMonth - 1;
      const y = currentMonth === 0 ? currentYear - 1 : currentYear;
      cells.push({ day, month: m, year: y, isCurrentMonth: false });
    }

    // Current month days
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({ day: d, month: currentMonth, year: currentYear, isCurrentMonth: true });
    }

    // Next month padding to fill up to 35 cells (5 rows)
    const remaining = 35 - cells.length;
    for (let d = 1; d <= remaining; d++) {
      const m = currentMonth === 11 ? 0 : currentMonth + 1;
      const y = currentMonth === 11 ? currentYear + 1 : currentYear;
      cells.push({ day: d, month: m, year: y, isCurrentMonth: false });
    }

    return cells;
  }, [currentYear, currentMonth]);

  // ─── Events grouped by date ──────────────────────────────────────────────────
  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach((evt) => {
      if (!map[evt.date]) map[evt.date] = [];
      map[evt.date].push(evt);
    });
    // Sort each day's events by start hour
    Object.keys(map).forEach((key) => {
      map[key].sort((a, b) => a.startHour - b.startHour);
    });
    return map;
  }, [events]);

  // ─── Upcoming events ────────────────────────────────────────────────────────
  const upcomingEvents = useMemo(() => {
    const todayStr = padDate(currentYear, currentMonth, selectedDay);
    return [...events]
      .filter((e) => e.date >= todayStr)
      .sort((a, b) => {
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        return a.startHour - b.startHour;
      })
      .slice(0, 5);
  }, [events, currentYear, currentMonth, selectedDay]);

  // ─── Navigation handlers ─────────────────────────────────────────────────────
  const goToToday = useCallback(() => {
    setCurrentYear(2025);
    setCurrentMonth(6);
    setSelectedDay(15);
    setSelectedWeekStart(13); // Sunday of the week containing July 15
  }, []);

  const navigatePrev = useCallback(() => {
    if (viewMode === 'month') {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear((y) => y - 1);
      } else {
        setCurrentMonth((m) => m - 1);
      }
    } else if (viewMode === 'week') {
      setSelectedWeekStart((d) => Math.max(1, d - 7));
    } else {
      setSelectedDay((d) => Math.max(1, d - 1));
    }
  }, [viewMode, currentMonth]);

  const navigateNext = useCallback(() => {
    if (viewMode === 'month') {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear((y) => y + 1);
      } else {
        setCurrentMonth((m) => m + 1);
      }
    } else if (viewMode === 'week') {
      setSelectedWeekStart((d) => Math.min(27, d + 7));
    } else {
      setSelectedDay((d) => Math.min(31, d + 1));
    }
  }, [viewMode, currentMonth]);

  // ─── Event handlers ──────────────────────────────────────────────────────────
  const handleEventClick = useCallback((evt) => {
    setSelectedEvent((prev) => (prev && prev.id === evt.id ? null : evt));
  }, []);

  const handleNewEventSave = useCallback(() => {
    if (!newEventTitle.trim()) return;
    const startH = newEventStartTime.length > 0 ? parseInt(newEventStartTime[0].id, 10) : 9;
    const endH = newEventEndTime.length > 0 ? parseInt(newEventEndTime[0].id, 10) : startH + 1;
    const newEvt = {
      id: `evt-new-${Date.now()}`,
      title: newEventTitle,
      date: newEventDate || padDate(currentYear, currentMonth, selectedDay),
      startHour: startH,
      endHour: Math.max(startH + 1, endH),
      color: SLDS_COLORS[Math.floor(Math.random() * SLDS_COLORS.length)],
      attendees: ['You'],
      location: newEventLocation || 'Zoom',
      description: newEventDescription,
    };
    setEvents((prev) => [...prev, newEvt]);
    setShowNewEventModal(false);
    setNewEventTitle('');
    setNewEventDate('2025-07-15');
    setNewEventStartTime([]);
    setNewEventEndTime([]);
    setNewEventLocation('');
    setNewEventDescription('');
  }, [newEventTitle, newEventDate, newEventStartTime, newEventEndTime, newEventLocation, newEventDescription, currentYear, currentMonth, selectedDay]);

  // ─── Drag & drop handlers ───────────────────────────────────────────────────
  const handleDragStart = useCallback((evt, e) => {
    setDraggedEvent(evt);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', evt.id);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback(
    (targetDateStr, e) => {
      e.preventDefault();
      if (!draggedEvent) return;
      setEvents((prev) =>
        prev.map((evt) =>
          evt.id === draggedEvent.id ? { ...evt, date: targetDateStr } : evt
        )
      );
      setDraggedEvent(null);
    },
    [draggedEvent]
  );

  // ─── Get title for toolbar ──────────────────────────────────────────────────
  const toolbarTitle = useMemo(() => {
    if (viewMode === 'month') {
      return `${MONTH_NAMES[currentMonth]} ${currentYear}`;
    }
    if (viewMode === 'week') {
      const startDay = selectedWeekStart;
      const endDay = Math.min(startDay + 6, getDaysInMonth(currentYear, currentMonth));
      return `Jul ${startDay} - ${endDay}, ${currentYear}`;
    }
    return `July ${selectedDay}, ${currentYear}`;
  }, [viewMode, currentMonth, currentYear, selectedWeekStart, selectedDay]);

  // ─── Render: Month View ──────────────────────────────────────────────────────
  const renderMonthView = () => {
    const todayStr = '2025-07-15';

    return (
      <div style={cardStyle}>
        {/* Day-of-week headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid #dddbda' }}>
          {DAY_NAMES.map((name) => (
            <div key={name} style={dayHeaderStyle}>
              {name}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
          {calendarGrid.map((cell, idx) => {
            const dateStr = padDate(cell.year, cell.month, cell.day);
            const dayEvents = eventsByDate[dateStr] || [];
            const isToday = dateStr === todayStr;
            const visibleEvents = dayEvents.slice(0, MAX_VISIBLE_EVENTS);
            const overflowCount = dayEvents.length - MAX_VISIBLE_EVENTS;

            return (
              <div
                key={idx}
                style={{
                  minHeight: '100px',
                  borderBottom: '1px solid #f0f0f0',
                  borderRight: idx % 7 < 6 ? '1px solid #f0f0f0' : 'none',
                  padding: '4px',
                  backgroundColor: isToday ? 'rgba(1,118,211,0.04)' : 'transparent',
                  opacity: cell.isCurrentMonth ? 1 : 0.4,
                  cursor: 'default',
                }}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(dateStr, e)}
              >
                {/* Day number */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '26px',
                    height: '26px',
                    borderRadius: '50%',
                    fontSize: '12px',
                    fontWeight: isToday ? '700' : '400',
                    color: isToday ? 'white' : cell.isCurrentMonth ? '#181818' : '#b0adab',
                    backgroundColor: isToday ? '#0176d3' : 'transparent',
                    marginBottom: '2px',
                  }}
                >
                  {cell.day}
                </div>

                {/* Event bars */}
                {visibleEvents.map((evt) => (
                  <div
                    key={evt.id}
                    draggable
                    onDragStart={(e) => handleDragStart(evt, e)}
                    onClick={() => handleEventClick(evt)}
                    style={{
                      ...eventBarStyle,
                      backgroundColor: evt.color,
                    }}
                    title={`${evt.title} (${formatHour(evt.startHour)} - ${formatHour(evt.endHour)})`}
                  >
                    {evt.title}
                  </div>
                ))}

                {overflowCount > 0 && (
                  <div
                    style={{
                      fontSize: '11px',
                      color: '#706e6b',
                      paddingLeft: '6px',
                      cursor: 'pointer',
                      fontWeight: '500',
                    }}
                    onClick={() => {
                      setSelectedDay(cell.day);
                      setViewMode('day');
                    }}
                  >
                    +{overflowCount} more
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Selected event detail */}
        {selectedEvent && (
          <div
            style={{
              marginTop: '16px',
              padding: '16px',
              backgroundColor: '#f3f3f3',
              borderRadius: '4px',
              border: `2px solid ${selectedEvent.color}`,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <div
                    style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: selectedEvent.color,
                    }}
                  />
                  <span style={{ fontSize: '16px', fontWeight: '700', color: '#181818' }}>
                    {selectedEvent.title}
                  </span>
                </div>
                <div style={{ fontSize: '13px', color: '#706e6b', marginBottom: '4px' }}>
                  <strong>Date:</strong> {selectedEvent.date} &nbsp;|&nbsp;
                  <strong>Time:</strong> {formatHour(selectedEvent.startHour)} - {formatHour(selectedEvent.endHour)}
                </div>
                <div style={{ fontSize: '13px', color: '#706e6b', marginBottom: '4px' }}>
                  <strong>Location:</strong> {selectedEvent.location}
                </div>
                <div style={{ fontSize: '13px', color: '#706e6b' }}>
                  <strong>Attendees:</strong> {selectedEvent.attendees.join(', ')}
                </div>
              </div>
              <Button
                assistiveText={{ icon: 'Close' }}
                iconCategory="utility"
                iconName="close"
                iconSize="small"
                variant="icon"
                onClick={() => setSelectedEvent(null)}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  // ─── Render: Week View ───────────────────────────────────────────────────────
  const renderWeekView = () => {
    const hours = Array.from({ length: 11 }, (_, i) => i + 8); // 8am to 6pm
    const weekDays = Array.from({ length: 7 }, (_, i) => {
      const day = selectedWeekStart + i;
      const daysInCurMonth = getDaysInMonth(currentYear, currentMonth);
      const actualDay = day > daysInCurMonth ? day - daysInCurMonth : day;
      return {
        day: actualDay,
        dateStr: padDate(currentYear, currentMonth, actualDay),
        dayName: DAY_NAMES[i],
      };
    });

    const HOUR_HEIGHT = 60;

    return (
      <div style={cardStyle}>
        {/* Week day headers */}
        <div style={{ display: 'grid', gridTemplateColumns: '60px repeat(7, 1fr)', borderBottom: '2px solid #dddbda' }}>
          <div style={{ padding: '8px' }} />
          {weekDays.map((wd) => (
            <div
              key={wd.dateStr}
              style={{
                ...dayHeaderStyle,
                borderLeft: '1px solid #f0f0f0',
                paddingBottom: '12px',
              }}
            >
              <div style={{ fontSize: '11px', color: '#706e6b' }}>{wd.dayName}</div>
              <div
                style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: '#181818',
                  marginTop: '2px',
                }}
              >
                {wd.day}
              </div>
            </div>
          ))}
        </div>

        {/* Hour grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '60px repeat(7, 1fr)',
            position: 'relative',
            maxHeight: '500px',
            overflowY: 'auto',
          }}
        >
          {/* Hour rows */}
          {hours.map((hour) => (
            <React.Fragment key={hour}>
              <div
                style={{
                  ...hourLabelStyle,
                  height: `${HOUR_HEIGHT}px`,
                  display: 'flex',
                  alignItems: 'flex-start',
                  paddingTop: '2px',
                  borderTop: '1px solid #f0f0f0',
                }}
              >
                {formatHour(hour)}
              </div>
              {weekDays.map((wd) => {
                const dayEvts = (eventsByDate[wd.dateStr] || []).filter(
                  (evt) => evt.startHour === hour
                );
                return (
                  <div
                    key={`${wd.dateStr}-${hour}`}
                    style={{
                      height: `${HOUR_HEIGHT}px`,
                      borderTop: '1px solid #f0f0f0',
                      borderLeft: '1px solid #f0f0f0',
                      position: 'relative',
                      padding: '1px',
                    }}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(wd.dateStr, e)}
                  >
                    {dayEvts.map((evt) => {
                      const duration = evt.endHour - evt.startHour;
                      return (
                        <div
                          key={evt.id}
                          draggable
                          onDragStart={(e) => handleDragStart(evt, e)}
                          onClick={() => handleEventClick(evt)}
                          style={{
                            position: 'absolute',
                            top: '1px',
                            left: '2px',
                            right: '2px',
                            height: `${duration * HOUR_HEIGHT - 4}px`,
                            backgroundColor: evt.color,
                            borderRadius: '4px',
                            padding: '4px 6px',
                            fontSize: '11px',
                            fontWeight: '600',
                            color: 'white',
                            cursor: 'pointer',
                            overflow: 'hidden',
                            zIndex: 2,
                            opacity: 0.9,
                          }}
                          title={`${evt.title} (${formatHour(evt.startHour)} - ${formatHour(evt.endHour)})`}
                        >
                          <div style={{ lineHeight: '1.2' }}>{evt.title}</div>
                          <div style={{ fontSize: '10px', opacity: 0.85, marginTop: '1px' }}>
                            {formatHour(evt.startHour)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>

        {/* Selected event detail (week) */}
        {selectedEvent && (
          <div
            style={{
              marginTop: '16px',
              padding: '16px',
              backgroundColor: '#f3f3f3',
              borderRadius: '4px',
              border: `2px solid ${selectedEvent.color}`,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: '16px', fontWeight: '700', marginBottom: '4px', color: '#181818' }}>
                  {selectedEvent.title}
                </div>
                <div style={{ fontSize: '13px', color: '#706e6b' }}>
                  {selectedEvent.date} &bull; {formatHour(selectedEvent.startHour)} -{' '}
                  {formatHour(selectedEvent.endHour)} &bull; {selectedEvent.location}
                </div>
              </div>
              <Button
                assistiveText={{ icon: 'Close' }}
                iconCategory="utility"
                iconName="close"
                iconSize="small"
                variant="icon"
                onClick={() => setSelectedEvent(null)}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  // ─── Render: Day View ────────────────────────────────────────────────────────
  const renderDayView = () => {
    const hours = Array.from({ length: 13 }, (_, i) => i + 7); // 7am to 7pm
    const dateStr = padDate(currentYear, currentMonth, selectedDay);
    const dayEvts = eventsByDate[dateStr] || [];
    const HOUR_HEIGHT = 70;

    return (
      <div style={cardStyle}>
        {/* Day header */}
        <div
          style={{
            padding: '12px 16px',
            borderBottom: '2px solid #dddbda',
            marginBottom: '0',
          }}
        >
          <span style={{ fontSize: '18px', fontWeight: '700', color: '#181818' }}>
            {DAY_NAMES[new Date(currentYear, currentMonth, selectedDay).getDay()]},{' '}
            {MONTH_NAMES[currentMonth]} {selectedDay}
          </span>
          <span style={{ fontSize: '13px', color: '#706e6b', marginLeft: '12px' }}>
            {dayEvts.length} event{dayEvts.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Timeline */}
        <div style={{ position: 'relative', maxHeight: '600px', overflowY: 'auto' }}>
          {hours.map((hour) => {
            const hourEvents = dayEvts.filter((evt) => evt.startHour === hour);
            return (
              <div
                key={hour}
                style={{
                  display: 'flex',
                  minHeight: `${HOUR_HEIGHT}px`,
                  borderBottom: '1px solid #f0f0f0',
                }}
              >
                {/* Hour label */}
                <div
                  style={{
                    ...hourLabelStyle,
                    paddingTop: '4px',
                    height: `${HOUR_HEIGHT}px`,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-end',
                  }}
                >
                  {formatHour(hour)}
                </div>

                {/* Event slot */}
                <div
                  style={{
                    flex: 1,
                    position: 'relative',
                    borderLeft: '1px solid #f0f0f0',
                    padding: '2px 8px',
                  }}
                >
                  {hourEvents.map((evt) => {
                    const duration = evt.endHour - evt.startHour;
                    return (
                      <div
                        key={evt.id}
                        onClick={() => handleEventClick(evt)}
                        style={{
                          backgroundColor: evt.color,
                          borderRadius: '6px',
                          padding: '10px 14px',
                          color: 'white',
                          cursor: 'pointer',
                          minHeight: `${duration * HOUR_HEIGHT - 12}px`,
                          marginBottom: '4px',
                        }}
                      >
                        <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '4px' }}>
                          {evt.title}
                        </div>
                        <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '2px' }}>
                          {formatHour(evt.startHour)} - {formatHour(evt.endHour)}
                        </div>
                        <div style={{ fontSize: '12px', opacity: 0.85, marginBottom: '2px' }}>
                          {evt.location}
                        </div>
                        <div style={{ fontSize: '11px', opacity: 0.8 }}>
                          {evt.attendees.join(', ')}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Selected event detail (day) */}
        {selectedEvent && (
          <div
            style={{
              marginTop: '16px',
              padding: '16px',
              backgroundColor: '#f3f3f3',
              borderRadius: '4px',
              border: `2px solid ${selectedEvent.color}`,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                  <div
                    style={{
                      width: '14px',
                      height: '14px',
                      borderRadius: '50%',
                      backgroundColor: selectedEvent.color,
                    }}
                  />
                  <span style={{ fontSize: '18px', fontWeight: '700', color: '#181818' }}>
                    {selectedEvent.title}
                  </span>
                </div>
                <div style={{ fontSize: '13px', color: '#706e6b', lineHeight: '1.8' }}>
                  <div>
                    <strong>Time:</strong> {formatHour(selectedEvent.startHour)} -{' '}
                    {formatHour(selectedEvent.endHour)}
                  </div>
                  <div>
                    <strong>Location:</strong> {selectedEvent.location}
                  </div>
                  <div>
                    <strong>Attendees:</strong> {selectedEvent.attendees.join(', ')}
                  </div>
                  {selectedEvent.description && (
                    <div>
                      <strong>Description:</strong> {selectedEvent.description}
                    </div>
                  )}
                </div>
              </div>
              <Button
                assistiveText={{ icon: 'Close' }}
                iconCategory="utility"
                iconName="close"
                iconSize="small"
                variant="icon"
                onClick={() => setSelectedEvent(null)}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  // ─── Render: Mini Calendar (Sidebar) ─────────────────────────────────────────
  const renderMiniCalendar = () => {
    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
    const cells = [];

    for (let i = 0; i < firstDay; i++) {
      cells.push(null);
    }
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(d);
    }

    return (
      <div style={cardStyle}>
        <div style={{ fontWeight: '700', fontSize: '13px', color: '#181818', marginBottom: '12px', textAlign: 'center' }}>
          {MONTH_NAMES[currentMonth]} {currentYear}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
          {/* Weekday headers */}
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
            <div
              key={i}
              style={{
                textAlign: 'center',
                fontSize: '10px',
                fontWeight: '700',
                color: '#b0adab',
                padding: '2px 0',
              }}
            >
              {d}
            </div>
          ))}

          {/* Day numbers */}
          {cells.map((day, idx) => {
            const isSelected = day === selectedDay;
            const isToday = day === 15; // July 15 = "today"
            const dateStr = day ? padDate(currentYear, currentMonth, day) : null;
            const hasEvents = dateStr && eventsByDate[dateStr] && eventsByDate[dateStr].length > 0;

            return (
              <div
                key={idx}
                onClick={() => {
                  if (day) {
                    setSelectedDay(day);
                    if (viewMode === 'day') {
                      // stay in day view, navigate
                    }
                  }
                }}
                style={{
                  textAlign: 'center',
                  fontSize: '11px',
                  padding: '3px 0',
                  cursor: day ? 'pointer' : 'default',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  lineHeight: '18px',
                  margin: '0 auto',
                  fontWeight: isToday ? '700' : '400',
                  color: day ? (isSelected ? 'white' : isToday ? '#0176d3' : '#181818') : 'transparent',
                  backgroundColor: isSelected ? '#0176d3' : 'transparent',
                  position: 'relative',
                }}
              >
                {day || ''}
                {hasEvents && !isSelected && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: '-1px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '4px',
                      height: '4px',
                      borderRadius: '50%',
                      backgroundColor: '#0176d3',
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ─── Render: Upcoming Events (Sidebar) ───────────────────────────────────────
  const renderUpcomingEvents = () => (
    <div style={cardStyle}>
      <div
        style={{
          fontWeight: '700',
          fontSize: '13px',
          color: '#181818',
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}
      >
        <Icon category="utility" name="clock" size="x-small" />
        Upcoming Events
      </div>
      {upcomingEvents.length === 0 && (
        <div style={{ fontSize: '12px', color: '#706e6b', fontStyle: 'italic' }}>
          No upcoming events
        </div>
      )}
      {upcomingEvents.map((evt) => (
        <div
          key={evt.id}
          onClick={() => handleEventClick(evt)}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
            padding: '8px 0',
            borderBottom: '1px solid #f0f0f0',
            cursor: 'pointer',
          }}
        >
          <div
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: evt.color,
              marginTop: '5px',
              flexShrink: 0,
            }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: '12px',
                fontWeight: '600',
                color: '#181818',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {evt.title}
            </div>
            <div style={{ fontSize: '11px', color: '#706e6b' }}>
              {evt.date.split('-').slice(1).join('/')} &bull; {formatHour(evt.startHour)}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // ─── Render: New Event Modal ─────────────────────────────────────────────────
  const renderNewEventModal = () => (
    <Modal
      isOpen={showNewEventModal}
      onRequestClose={() => setShowNewEventModal(false)}
      heading="New Event"
      footer={[
        <Button key="cancel" label="Cancel" onClick={() => setShowNewEventModal(false)} />,
        <Button key="save" label="Save Event" variant="brand" onClick={handleNewEventSave} />,
      ]}
      size="small"
    >
      <div style={{ padding: '16px' }}>
        <div style={{ marginBottom: '16px' }}>
          <Input
            label="Title"
            placeholder="Enter event title"
            value={newEventTitle}
            onChange={(e) => setNewEventTitle(e.target.value)}
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <Input
            label="Date"
            placeholder="YYYY-MM-DD"
            value={newEventDate}
            onChange={(e) => setNewEventDate(e.target.value)}
          />
        </div>

        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
          <div style={{ flex: 1 }}>
            <Combobox
              labels={{ label: 'Start Time', placeholder: 'Select time' }}
              options={HOUR_OPTIONS}
              selection={newEventStartTime}
              events={{
                onSelect: (e, data) => setNewEventStartTime(data.selection),
              }}
              variant="readonly"
            />
          </div>
          <div style={{ flex: 1 }}>
            <Combobox
              labels={{ label: 'End Time', placeholder: 'Select time' }}
              options={HOUR_OPTIONS}
              selection={newEventEndTime}
              events={{
                onSelect: (e, data) => setNewEventEndTime(data.selection),
              }}
              variant="readonly"
            />
          </div>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <Input
            label="Location"
            placeholder="e.g. Conference Room A, Zoom"
            value={newEventLocation}
            onChange={(e) => setNewEventLocation(e.target.value)}
          />
        </div>

        <div style={{ marginBottom: '8px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '12px',
              fontWeight: '700',
              color: '#181818',
              marginBottom: '4px',
            }}
          >
            Description
          </label>
          <textarea
            placeholder="Add event description..."
            value={newEventDescription}
            onChange={(e) => setNewEventDescription(e.target.value)}
            style={{
              width: '100%',
              minHeight: '80px',
              padding: '8px 12px',
              borderRadius: '4px',
              border: '1px solid #dddbda',
              fontSize: '13px',
              fontFamily: 'inherit',
              resize: 'vertical',
              outline: 'none',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#0176d3';
              e.target.style.boxShadow = '0 0 3px 0 #0176d3';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#dddbda';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>
      </div>
    </Modal>
  );

  // ─── Render: View toggle helpers ─────────────────────────────────────────────
  const renderViewButton = (label, mode) => (
    <Button
      key={mode}
      label={label}
      variant={viewMode === mode ? 'brand' : 'neutral'}
      onClick={() => {
        setViewMode(mode);
        setSelectedEvent(null);
      }}
    />
  );

  // ─── Main render ─────────────────────────────────────────────────────────────
  return (
    <ShowcasePageWrapper
      title="Calendar & Scheduling"
      description="Full calendar with month, week, and day views"
      icon="event"
      iconCategory="standard"
    >
      {/* Toolbar */}
      <div style={toolbarStyle}>
        {/* Left: Today + Navigation */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Button label="Today" variant="neutral" onClick={goToToday} />
          <Button
            assistiveText={{ icon: 'Previous' }}
            iconCategory="utility"
            iconName="chevronleft"
            iconSize="medium"
            variant="icon"
            onClick={navigatePrev}
          />
          <Button
            assistiveText={{ icon: 'Next' }}
            iconCategory="utility"
            iconName="chevronright"
            iconSize="medium"
            variant="icon"
            onClick={navigateNext}
          />
          <span
            style={{
              fontSize: '18px',
              fontWeight: '700',
              color: '#181818',
              marginLeft: '8px',
            }}
          >
            {toolbarTitle}
          </span>
        </div>

        {/* Right: View toggle + New Event */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <ButtonGroup>
            {renderViewButton('Month', 'month')}
            {renderViewButton('Week', 'week')}
            {renderViewButton('Day', 'day')}
          </ButtonGroup>
          <Button
            label="New Event"
            variant="brand"
            iconCategory="utility"
            iconName="add"
            iconPosition="left"
            onClick={() => setShowNewEventModal(true)}
          />
        </div>
      </div>

      {/* Main content area: Calendar + Sidebar */}
      <div style={{ display: 'flex', gap: '16px' }}>
        {/* Calendar area */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {viewMode === 'month' && renderMonthView()}
          {viewMode === 'week' && renderWeekView()}
          {viewMode === 'day' && renderDayView()}
        </div>

        {/* Sidebar */}
        <div style={sidebarStyle}>
          {renderMiniCalendar()}
          {renderUpcomingEvents()}
        </div>
      </div>

      {/* New Event Modal */}
      {renderNewEventModal()}
    </ShowcasePageWrapper>
  );
};

export default CalendarScheduling;
